import {
    HttpClient,
    HttpErrorResponse,
    HttpHeaders,
} from "@angular/common/http";
import { inject, Injectable, signal } from "@angular/core";
import { firstValueFrom, timeout } from "rxjs";
import { TranslateService } from "@ngx-translate/core";
import { SessionFacade } from "../../features/authentication";
import { APP_CONFIG } from "./app-config";

interface RuntimeEnvironment {
    NG_GEMVISION_API_KEY?: string;
    NG_GEMVISION_READONLY_API_KEY?: string;
    NG_APP_RECAPTCHA_KEY?: string;
    NG_APP_PAYSYS_KEY?: string;
    NG_APP_WEBRTC_URL?: string;
    NG_APP_WEBRTC_PATH?: string;
    NG_APP_MEDIA_CONFERENCE_URL?: string;
}

export type AppInitializationError =
    | "configuration"
    | "network"
    | "server"
    | "unknown";

const INITIALIZATION_TIMEOUT_MS = 15_000;

class RuntimeConfigurationError extends Error {}

@Injectable({ providedIn: "root" })
export class AppInitializationService {
    private readonly http = inject(HttpClient);
    private readonly config = inject(APP_CONFIG);
    private readonly session = inject(SessionFacade);
    private readonly translate = inject(TranslateService);
    private readonly statusState = signal<"loading" | "ready" | "failed">(
        "loading"
    );
    private readonly errorState = signal<AppInitializationError | null>(null);
    private inFlight: Promise<void> | null = null;

    readonly status = this.statusState.asReadonly();
    readonly error = this.errorState.asReadonly();

    initialize(): Promise<void> {
        if (this.statusState() === "ready") return Promise.resolve();
        if (this.inFlight) return this.inFlight;

        this.statusState.set("loading");
        this.errorState.set(null);
        this.inFlight = this.runInitialization()
            .catch((error: unknown) => {
                this.errorState.set(this.classifyError(error));
                this.statusState.set("failed");
                console.error("Application initialization failed", error);
            })
            .finally(() => {
                this.inFlight = null;
            });
        return this.inFlight;
    }

    retry(): void {
        void this.initialize();
    }

    private async runInitialization(): Promise<void> {
        await firstValueFrom(
            this.translate.use("en").pipe(timeout(INITIALIZATION_TIMEOUT_MS))
        );
        this.translate.setDefaultLang("en");
        let language = "en";
        try { if (localStorage.getItem("dashboard-next-language") === "nl") language = "nl"; } catch { /* Use English when storage is unavailable. */ }
        if (language === "nl") {
            try { await firstValueFrom(this.translate.use("nl").pipe(timeout(INITIALIZATION_TIMEOUT_MS))); }
            catch { language = "en"; await firstValueFrom(this.translate.use("en")); }
        }
        document.documentElement.lang = language;
        const runtime = await firstValueFrom(
            this.http
                .get<RuntimeEnvironment>("/environment.json")
                .pipe(timeout(INITIALIZATION_TIMEOUT_MS))
        );
        const apiKey = runtime.NG_GEMVISION_API_KEY?.trim() ?? "";
        const readonlyApiKey =
            runtime.NG_GEMVISION_READONLY_API_KEY?.trim() || apiKey;
        if (!apiKey || !readonlyApiKey) {
            throw new RuntimeConfigurationError(
                "The Gemvision runtime API keys are missing."
            );
        }

        this.config.apiKey = apiKey;
        this.config.readonlyApiKey = readonlyApiKey;
        this.config.recaptchaSiteKey = runtime.NG_APP_RECAPTCHA_KEY ?? "";
        this.config.stripePublishableKey = runtime.NG_APP_PAYSYS_KEY ?? "";
        this.config.realtimeBaseUrl =
            runtime.NG_APP_WEBRTC_URL || this.config.realtimeBaseUrl;
        this.config.realtimePath =
            runtime.NG_APP_WEBRTC_PATH || this.config.realtimePath;
        this.config.mediaConferenceBaseUrl =
            runtime.NG_APP_MEDIA_CONFERENCE_URL ||
            this.config.mediaConferenceBaseUrl;

        const headers = new HttpHeaders({
            "x-gem-api-key": this.config.readonlyApiKey,
        });
        const version = await firstValueFrom(
            this.http
                .get<number>(
                    `${this.config.apiBaseUrl}/servauthen/app_version/x-version-web`,
                    { headers }
                )
                .pipe(timeout(INITIALIZATION_TIMEOUT_MS))
        );
        this.config.webVersion = String(version);
        this.statusState.set("ready");
        this.session.initialize();
    }

    private classifyError(error: unknown): AppInitializationError {
        if (error instanceof RuntimeConfigurationError) return "configuration";
        if (error instanceof HttpErrorResponse) {
            return error.status === 0 ? "network" : "server";
        }
        if (error instanceof Error && error.name === "TimeoutError") {
            return "network";
        }
        return "unknown";
    }
}
